import { z } from 'zod';
import { isoDateSchema } from '../api';
// ── Laboratory quality control (SRS 8.1) ─────────────────────────────────────
export const QC_LEVEL = ['low', 'normal', 'high'];
/**
 * The Westgard rules this system evaluates.
 *
 * Only the classic multirule set, because these are the rules a bench actually
 * runs and every one of them can be decided from the last ten points. A rule
 * nobody can explain at the bench is a rule that gets overridden.
 */
export const WESTGARD_RULES = ['1_2s', '1_3s', '2_2s', 'R_4s', '4_1s', '10x'];
export const WESTGARD_DESCRIPTIONS = {
    '1_2s': 'One control beyond 2 SD — a warning, not a rejection',
    '1_3s': 'One control beyond 3 SD — random error, reject the run',
    '2_2s': 'Two consecutive controls beyond the same 2 SD limit — systematic error',
    R_4s: 'One control beyond +2 SD and another beyond -2 SD — random error',
    '4_1s': 'Four consecutive controls beyond the same 1 SD limit — systematic drift',
    '10x': 'Ten consecutive controls on the same side of the mean — a shift',
};
export const upsertControlLotSchema = z.object({
    testId: z.string().uuid(),
    level: z.enum(QC_LEVEL).default('normal'),
    lotNo: z.string().trim().min(1).max(60),
    manufacturer: z.string().trim().max(160).optional(),
    expiryDate: isoDateSchema,
    targetMean: z.number(),
    /** Must be positive: a zero SD makes every z-score infinite. */
    targetSd: z.number().gt(0),
    isActive: z.boolean().default(true),
});
export const recordQcResultSchema = z.object({
    controlLotId: z.string().uuid(),
    value: z.number(),
    instrument: z.string().trim().max(80).optional(),
    comment: z.string().max(600).optional(),
    runAt: z.string().datetime().optional(),
});
// ── Reflex rules and send-out tests (SRS 8.1) ────────────────────────────────
/**
 * "When this test comes back like this, order that one too."
 *
 * Kept as data on the test master rather than as code so a pathologist can
 * change a reflex without a deployment.
 */
export const reflexRuleSchema = z.object({
    when: z.enum(['high', 'low', 'critical_high', 'critical_low', 'abnormal', 'above', 'below']),
    /** Threshold for `above` / `below`; ignored for the flag-based conditions. */
    value: z.number().optional(),
    /** Test codes to add to the same order automatically. */
    addTestCodes: z.array(z.string().trim().min(2).max(24)).min(1).max(10),
    note: z.string().max(200).optional(),
});
export const setReflexRulesSchema = z.object({
    rules: z.array(reflexRuleSchema).max(10),
});
export const sendOutSchema = z.object({
    orderItemIds: z.array(z.string().uuid()).min(1).max(50),
    referenceLab: z.string().trim().min(2).max(160),
    externalRef: z.string().trim().max(80).optional(),
    notes: z.string().max(600).optional(),
});
//# sourceMappingURL=qc.js.map